import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from "lucide-react";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    "Plumbing",
    "Electrical",
    "Roofing",
    "Cleaning",
    "Lawn Care",
    "HVAC",
    "Pool Maintenance",
  ];

  const quickLinks = [
    { href: "/", label: "Home" },
    { href: "/services", label: "Services" },
    { href: "/booking", label: "Book Now" },
    { href: "/pay-invoice", label: "Pay Invoice" },
    { href: "/contact", label: "Contact" },
  ];

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="/protocall-logo.png" 
                alt="ProToCall" 
                className="h-14 w-auto brightness-0 invert"
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Your trusted partner for residential and commercial property maintenance. Professional service, guaranteed quality.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://facebook.com/cariphs"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-orange-500 transition-colors"
                aria-label="Facebook"
                data-testid="footer-social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com/cariphs"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-orange-500 transition-colors"
                aria-label="Instagram"
                data-testid="footer-social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="text-slate-400 hover:text-orange-500 transition-colors" aria-label="LinkedIn">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-lg mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Services
            </h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <Link
                    to="/services"
                    className="text-slate-400 hover:text-orange-500 transition-colors text-sm"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-orange-500 transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/login"
                  className="text-slate-400 hover:text-orange-500 transition-colors text-sm"
                >
                  Client Login
                </Link>
              </li>
            </ul>
            <div className="mt-6 pt-6 border-t border-slate-800">
              <h5 className="font-medium text-sm mb-3">Contact Us</h5>
              <div className="space-y-2 text-sm text-slate-400">
                <a href="tel:+17587230202" className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                  <Phone className="w-4 h-4" />
                  +17587230202
                </a>
                <a href="mailto:info@cariphs.com" className="flex items-center gap-2 hover:text-orange-500 transition-colors">
                  <Mail className="w-4 h-4" />
                  info@cariphs.com
                </a>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5" />
                  <span>Saint Lucia, Caribbean</span>
                </div>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-semibold text-lg mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Stay Updated
            </h4>
            <p className="text-slate-400 text-sm mb-4">
              Subscribe to get special offers and maintenance tips.
            </p>
            <form className="space-y-3" onSubmit={(e) => e.preventDefault()}>
              <Input
                type="email"
                placeholder="Enter your email"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-11"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-11"
                data-testid="newsletter-submit-btn"
              >
                Subscribe
              </Button>
            </form>
            <p className="text-xs text-slate-500 mt-4">
              Serving both residential and commercial clients in Saint Lucia and the Caribbean.
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-400 text-sm">
            © {currentYear} ProToCall. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <a href="#" className="hover:text-orange-500 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-orange-500 transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
